import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  FullscreenSpinner,
} from '@shared/ui';
import { productApi } from '@entities/product';
import { MediaDownloadLink, MediaImage } from '@entities/media';
import { pickLocalized, useCurrentLocale } from '@shared/i18n';
import { formatDateTime, formatMoney } from '@shared/lib/format';
import { ROUTES } from '@shared/config/routes';
import { ProductActionsPanel } from '@features/product-moderation';

export function ProductDetailPage() {
  const locale = useCurrentLocale();
  const { id = '' } = useParams<{ id: string }>();
  const product = useQuery({
    queryKey: ['product', id],
    queryFn: () => productApi.get(id),
    enabled: !!id,
  });

  if (product.isLoading) return <FullscreenSpinner />;
  if (product.isError || !product.data) return <EmptyState title="Не найдено" />;

  const p = product.data;
  const m = p.manufacturer;

  return (
    <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">
          {pickLocalized(p.name, locale, '—')}
        </h1>

        {p.photos.length > 0 ? (
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            {p.photos.map((photo) => (
              <MediaImage
                key={photo.id}
                link={photo}
                alt=""
                className="aspect-square w-full rounded-md border object-cover"
              />
            ))}
          </div>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>Описание</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="whitespace-pre-wrap text-sm">
              {pickLocalized(p.description, locale, '—')}
            </p>
            <details className="text-xs text-muted-foreground">
              <summary className="cursor-pointer select-none">Показать все локали</summary>
              <dl className="mt-2 space-y-2">
                {(['ru', 'en', 'uz'] as const).map((loc) => (
                  <div key={loc}>
                    <dt className="font-mono text-[10px] uppercase">{loc}</dt>
                    <dd className="mt-0.5 whitespace-pre-wrap text-foreground">
                      <span className="font-medium">{p.name?.[loc] || '—'}</span>
                      <div className="text-muted-foreground">{p.description?.[loc] || '—'}</div>
                    </dd>
                  </div>
                ))}
              </dl>
            </details>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Характеристики</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-muted-foreground">MOQ</dt>
              <dd>{p.moq ?? '—'}</dd>
              <dt className="text-muted-foreground">Export price</dt>
              <dd>{formatMoney(p.exportPrice)}</dd>
              <dt className="text-muted-foreground">HS-код</dt>
              <dd>{p.hsCode ? <span className="font-mono">{p.hsCode}</span> : '—'}</dd>
              <dt className="text-muted-foreground">Страна</dt>
              <dd>{p.originCountry}</dd>
              <dt className="text-muted-foreground">Stock</dt>
              <dd>
                {p.stock ? (
                  <Badge variant="secondary">
                    stock{p.stockDiscountPct ? ` −${p.stockDiscountPct}%` : ''}
                  </Badge>
                ) : (
                  '—'
                )}
              </dd>
              <dt className="text-muted-foreground">Eco</dt>
              <dd>{p.eco ? <Badge variant="success">eco</Badge> : '—'}</dd>
              <dt className="text-muted-foreground">Создан</dt>
              <dd>{formatDateTime(p.createdAt)}</dd>
              <dt className="text-muted-foreground">Обновлён</dt>
              <dd>{formatDateTime(p.updatedAt)}</dd>
            </dl>
          </CardContent>
        </Card>

        {p.ecoCertificates.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>Эко-сертификаты</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-1 text-sm">
                {p.ecoCertificates.map((c) => (
                  <li key={c.id}>
                    <MediaDownloadLink link={c}>
                      Сертификат · {c.contentType?.split('/')[1] ?? 'file'}
                    </MediaDownloadLink>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : null}
      </div>

      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Производитель</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-start gap-3">
              <MediaImage
                link={m.logo}
                alt={m.companyName}
                className="h-12 w-12 shrink-0 rounded-md border bg-muted object-cover"
              />
              <div className="min-w-0 space-y-1">
                <Link
                  to={ROUTES.manufacturerDetail(m.id)}
                  className="block truncate font-medium text-primary hover:underline"
                >
                  {m.companyName}
                </Link>
                <div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
                  {m.country ? <span>{m.country}</span> : null}
                  <Badge variant="outline" className="text-[10px]">
                    KYC · {m.kycStatus}
                  </Badge>
                  {m.ecoProducer ? (
                    <Badge variant="success" className="text-[10px]">
                      eco
                    </Badge>
                  ) : null}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <ProductActionsPanel product={p} />
      </div>
    </div>
  );
}
